import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import type { Membership } from '../../../lib/api';

/**
 * Per-tier `indirect` editor (Phase 12B). Reads
 *   memberships[receiver].config.computation.indirect[level][buyerTier]
 * — one extra dimension on top of `direct` because indirect pays L2..N.
 *
 * UI: tabs per level (L2, L3, …); each tab is a 2D receiver × buyer
 * matrix like the direct editor. A level is "present" if any tier's
 * config has a row for that level OR if the admin explicitly added it.
 */
export function IndirectMatrixPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  // Discover levels present across all tiers (and remember admin-added ones).
  const presentLevels = useMemo(() => {
    const set = new Set<number>();
    for (const cfg of Object.values(draft)) {
      const computation = cfg.computation as Record<string, unknown> | undefined;
      const indirect = computation?.indirect as Record<string, unknown> | undefined;
      if (indirect) {
        for (const k of Object.keys(indirect)) {
          const n = Number(k);
          if (Number.isFinite(n) && n >= 2) set.add(n);
        }
      }
    }
    if (set.size === 0) set.add(2);
    return Array.from(set).sort((a, b) => a - b);
  }, [draft]);

  const [extraLevels, setExtraLevels] = useState<number[]>([]);
  const levels = useMemo(() => {
    const set = new Set<number>(presentLevels);
    for (const n of extraLevels) set.add(n);
    return Array.from(set).sort((a, b) => a - b);
  }, [presentLevels, extraLevels]);

  const [active, setActive] = useState<number>(levels[0] ?? 2);

  function getCell(
    receiverCfg: Record<string, unknown>,
    level: number,
    buyerCode: string,
  ): number {
    const computation = (receiverCfg.computation as Record<string, unknown> | undefined) ?? {};
    const indirect = (computation.indirect as Record<string, unknown> | undefined) ?? {};
    const perLevel = (indirect[String(level)] as Record<string, unknown> | undefined) ?? {};
    const v = perLevel[buyerCode];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }

  function setCell(membershipId: string, level: number, buyerCode: string, n: number) {
    const current = draft[membershipId] ?? {};
    const computation = ((current.computation as Record<string, unknown>) ?? {}) as Record<
      string,
      unknown
    >;
    const indirect = {
      ...((computation.indirect as Record<string, unknown> | undefined) ?? {}),
    } as Record<string, unknown>;
    const perLevel = {
      ...((indirect[String(level)] as Record<string, unknown> | undefined) ?? {}),
    } as Record<string, unknown>;
    if (n === 0) {
      delete perLevel[buyerCode];
    } else {
      perLevel[buyerCode] = n;
    }
    if (Object.keys(perLevel).length === 0) delete indirect[String(level)];
    else indirect[String(level)] = perLevel;

    const nextComputation = { ...computation };
    if (Object.keys(indirect).length === 0) delete nextComputation.indirect;
    else nextComputation.indirect = indirect;

    const nextCfg = { ...current };
    if (Object.keys(nextComputation).length === 0) delete nextCfg.computation;
    else nextCfg.computation = nextComputation;
    onChange(membershipId, nextCfg);
  }

  function getRequirement(receiverCfg: Record<string, unknown>, level: number): number {
    const computation = (receiverCfg.computation as Record<string, unknown> | undefined) ?? {};
    const req = (computation.indirect_requirement as Record<string, unknown> | undefined) ?? {};
    const v = req[String(level)];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }

  function setRequirement(membershipId: string, level: number, n: number) {
    const current = draft[membershipId] ?? {};
    const computation = ((current.computation as Record<string, unknown>) ?? {}) as Record<
      string,
      unknown
    >;
    const req = {
      ...((computation.indirect_requirement as Record<string, unknown> | undefined) ?? {}),
    } as Record<string, unknown>;
    if (n <= 0) delete req[String(level)];
    else req[String(level)] = n;

    const nextComputation = { ...computation };
    if (Object.keys(req).length === 0) delete nextComputation.indirect_requirement;
    else nextComputation.indirect_requirement = req;

    const nextCfg = { ...current };
    if (Object.keys(nextComputation).length === 0) delete nextCfg.computation;
    else nextCfg.computation = nextComputation;
    onChange(membershipId, nextCfg);
  }

  function addLevel() {
    const next = Math.max(...levels, 1) + 1;
    setExtraLevels((prev) => [...prev, next]);
    setActive(next);
  }

  function removeLevel(level: number) {
    // Strip the level from every tier's config.
    for (const m of memberships) {
      const current = draft[m.id] ?? {};
      const computation = (current.computation as Record<string, unknown>) ?? {};
      const indirect = ((computation.indirect as Record<string, unknown>) ?? {}) as Record<
        string,
        unknown
      >;
      const req = ((computation.indirect_requirement as Record<string, unknown>) ?? {}) as Record<
        string,
        unknown
      >;
      const hasIndirect = indirect[String(level)] !== undefined;
      const hasReq = req[String(level)] !== undefined;
      if (!hasIndirect && !hasReq) continue;
      const nextComputation = { ...computation };
      if (hasIndirect) {
        const nextIndirect = { ...indirect };
        delete nextIndirect[String(level)];
        if (Object.keys(nextIndirect).length === 0) delete nextComputation.indirect;
        else nextComputation.indirect = nextIndirect;
      }
      if (hasReq) {
        const nextReq = { ...req };
        delete nextReq[String(level)];
        if (Object.keys(nextReq).length === 0) delete nextComputation.indirect_requirement;
        else nextComputation.indirect_requirement = nextReq;
      }
      const nextCfg = { ...current };
      if (Object.keys(nextComputation).length === 0) delete nextCfg.computation;
      else nextCfg.computation = nextComputation;
      onChange(m.id, nextCfg);
    }
    setExtraLevels((prev) => prev.filter((l) => l !== level));
    if (active === level) {
      const remaining = levels.filter((l) => l !== level);
      setActive(remaining[0] ?? 2);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-muted-foreground">
        Per-level indirect amounts. Rows = receiver tier; columns = buyer
        tier; cell = flat amount paid to ancestors at that level (level 2
        is the sponsor's sponsor, etc.). <b>Unlock ≥</b> sets how many PAID
        directs the receiver needs before they earn at this level (0 = no gate).
      </p>

      <div className="flex items-center gap-1 flex-wrap">
        {levels.map((lvl) => (
          <button
            key={lvl}
            type="button"
            onClick={() => setActive(lvl)}
            className={cn(
              'h-8 px-3 rounded-md text-xs font-mono border transition-colors',
              active === lvl
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-input text-muted-foreground hover:bg-muted',
            )}
          >
            Level {lvl}
          </button>
        ))}
        {!readOnly ? (
          <>
            <Button size="sm" variant="outline" onClick={addLevel}>
              <Plus className="h-3 w-3 mr-1" />
              Level
            </Button>
            {levels.length > 1 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeLevel(active)}
                className="text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Remove L{active}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">
                Receiver \ Buyer
              </th>
              <th
                className="px-3 py-2 font-medium whitespace-nowrap"
                title="Min PAID directs the receiver needs to earn at this level (0 = no gate)"
              >
                Unlock ≥
              </th>
              {memberships.map((m) => (
                <th key={m.code} className="px-3 py-2 font-mono font-medium" title={m.name}>
                  {m.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((receiver) => (
              <tr key={receiver.id} className="border-t">
                <td
                  className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                  title={receiver.name}
                >
                  {receiver.code}
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number"
                    className="h-8 w-16 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                    value={getRequirement(draft[receiver.id] ?? {}, active) || ''}
                    placeholder="0"
                    disabled={readOnly}
                    step={1}
                    min={0}
                    onChange={(e) =>
                      setRequirement(receiver.id, active, Math.floor(Number(e.target.value) || 0))
                    }
                  />
                </td>
                {memberships.map((buyer) => {
                  const cell = getCell(draft[receiver.id] ?? {}, active, buyer.code);
                  return (
                    <td key={buyer.code} className="px-2 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={cell}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setCell(receiver.id, active, buyer.code, Number(e.target.value) || 0)
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
